export const ROLES = {
  ACCOUNT_OWNER: 'account-owner',
  BILLING_ADMIN: 'billing-admin', 
  LICENSE_ADMIN: 'license-admin',
  REGISTERED_CONTACT: 'registered-contact'
};

export const ALL_ROLES = Object.values(ROLES);

// Helper function to check user roles
export const hasRole = (userRoles, role) => {
  return userRoles?.includes(role);
};

// Specific role check functions
export const hasAccountOwnerRole = (userRoles) => {
  return hasRole(userRoles, ROLES.ACCOUNT_OWNER);
};

export const hasBillingAdminRole = (userRoles) => {
  return hasRole(userRoles, ROLES.BILLING_ADMIN);
};

export const hasLicenseAdminRole = (userRoles) => {
  return hasRole(userRoles, ROLES.LICENSE_ADMIN);
};

export const hasRegisteredContactRole = (userRoles) => {
  return hasRole(userRoles, ROLES.REGISTERED_CONTACT);
};

// Capabilities for the Manage Licenses drawer (<ManageSeats>). EVERY page that renders the
// drawer must spread these — the component's prop defaults deny, so a call site that omits
// them gets a read-only drawer rather than the wrong role's controls.
//
// `hasRoleFn` is the single-argument checker from useUserStore (`hasRole(role)`), not the
// two-argument `hasRole(userRoles, role)` helper above.
export function getLicenseCapabilities(hasRoleFn) {
  // Deny by default if the caller hands us nothing usable. Failing closed keeps a store-shape
  // change from silently granting the full Account Owner drawer to everyone.
  const has = typeof hasRoleFn === 'function' ? hasRoleFn : () => false;
  const isAccountOwner = has(ROLES.ACCOUNT_OWNER);
  const isBillingAdmin = has(ROLES.BILLING_ADMIN);
  const isLicenseAdmin = has(ROLES.LICENSE_ADMIN);

  return {
    // Whether the "Manage Licenses" / "Manage Seats" trigger renders at all.
    canOpenManageLicenses: isAccountOwner || isBillingAdmin || isLicenseAdmin,
    // Seat-count controls (Add Seats, stepper, Apply, order summary) — billing roles only.
    canUpdateSeats: isAccountOwner || isBillingAdmin,
    // Staging assign/unassign. A Billing Admin buys seats but never hands them out.
    canAssignLicenses: isAccountOwner || isLicenseAdmin,
    // `confirmAssignments` used to live here: true for a License Admin, so only they were asked
    // before Save committed. Every role is asked now — an unassign takes someone's access away the
    // moment it lands — so there is nothing left for the flag to decide. Manage Licenses confirms
    // unconditionally; on a seat increase the pay dialog carries the read-back rather than stacking
    // a second dialog on top of it.
  };
}

// Role display names ("License Admin") and codes ("license-admin") both turn up in API payloads —
// the Team listing and the dashboard contacts carry display names, the token carries codes.
export const normalizeRoleCode = (role) => (role || '').trim().toLowerCase().replaceAll(' ', '-');

// "Registered Contact" is the implicit baseline every user holds — internal detail, never shown.
// A non-elevated contact simply has no role badges. Accepts the Team listing's roles CSV or an
// array (dashboard contacts) of display names; returns the display names that are left.
export function getElevatedRoles(roles) {
  let list = [];
  if (Array.isArray(roles)) {
    list = roles;
  } else if (typeof roles === 'string') {
    list = roles.split(',');
  }
  return list
    .map((r) => (r || '').trim())
    .filter(Boolean)
    .filter((r) => normalizeRoleCode(r) !== ROLES.REGISTERED_CONTACT);
}

/**
 * Whether the ACTOR may edit one particular listed user.
 *
 * Only Account Owner and License Admin may edit users at all; Billing Admin and Registered Contact
 * are view-only. An Account Owner edits everyone, themselves included. A License Admin may edit a
 * plain contact and their OWN row, but nobody else holding an elevated role — another Account
 * Owner, Billing Admin or License Admin outranks or equals them.
 *
 * The self exception exists because the elevated-role check would otherwise lock a License Admin
 * out of their own details (name, phone, DataNet) purely for holding the role that lets them edit
 * at all. Editing their own row cannot escalate anything: the elevated-permissions section is
 * never rendered for a License Admin, and the server refuses any elevated-role change they send
 * (RolePermissionPolicy.ValidateChange).
 *
 * The server enforces the same rule: RolePermissionPolicy.CanEditTargetUser runs in
 * EditUserService (PUT /user/{id}) and UserStatusService (deactivate/reactivate) and returns 403
 * when a License Admin targets another elevated user. This helper only keeps the UI in step with
 * it, so a hidden action here is never the authorization boundary.
 *
 * @param {object} params
 * @param {string[]} params.actorRoles the signed-in user's role codes
 * @param {number|string|null} [params.actorUserId] the signed-in user's portal id
 * @param {number|string|null} [params.targetUserId] the listed user's portal id
 * @param {string|string[]} [params.targetRoles] the listed user's roles (CSV or array)
 */
export function canEditTargetUser({ actorRoles, actorUserId, targetUserId, targetRoles }) {
  const isAccountOwner = hasAccountOwnerRole(actorRoles);
  if (!isAccountOwner && !hasLicenseAdminRole(actorRoles)) return false;
  if (isAccountOwner) return true;

  const isSelf =
    actorUserId != null && targetUserId != null && String(actorUserId) === String(targetUserId);
  return isSelf || getElevatedRoles(targetRoles).length === 0;
}
